import { useState } from 'react'

import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  useReviewQueueQuery,
  useReviewerCapabilityQuery
} from 'shared/api/forgeApi'

import { MarketplacePageHeader } from '../../../MarketplacePageHeader'
import { ReviewArtifactCard } from './ReviewArtifactCard'
import styles from './ReviewQueue.module.scss'

type Tab = 'pending' | 'approved'

// reviewer-only approval queue, shown as its own marketplace page
export const ReviewSection = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'widgets.aiAgent'
  })
  const { data: capability } = useReviewerCapabilityQuery()
  const isReviewer = Boolean(capability?.is_reviewer)

  const [tab, setTab] = useState<Tab>('pending')

  const { data: pending = [] } = useReviewQueueQuery('pending', {
    enabled: isReviewer
  })
  const { data: approved = [] } = useReviewQueueQuery('approved', {
    enabled: isReviewer
  })

  if (!isReviewer) return null

  const list = tab === 'pending' ? pending : approved

  return (
    <div className={styles.section}>
      <MarketplacePageHeader
        title={t('review_section_title')}
        subtitle={t('review_section_hint')}
      >
        <div
          className={styles.tabs}
          role='tablist'
          aria-label={t('review_section_title')}
        >
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'pending'}
            className={cn(styles.tab, {
              [styles.tabActive]: tab === 'pending'
            })}
            onClick={() => setTab('pending')}
          >
            {t('review_tab_pending')} ({pending.length})
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'approved'}
            className={cn(styles.tab, {
              [styles.tabActive]: tab === 'approved'
            })}
            onClick={() => setTab('approved')}
          >
            {t('review_tab_approved')} ({approved.length})
          </button>
        </div>
      </MarketplacePageHeader>

      {list.length === 0 ? (
        <p className={styles.empty}>
          {tab === 'pending' ? t('review_empty') : t('review_none_approved')}
        </p>
      ) : (
        <div className={styles.queue}>
          {list.map(artifact => (
            <ReviewArtifactCard key={artifact.public_id} artifact={artifact} />
          ))}
        </div>
      )}
    </div>
  )
}
